import { useCallback, useMemo } from "react"
import { Formik, Form, FormikHelpers } from "formik"
import { useNavigate } from "react-router"
import { useQueryClient } from "@tanstack/react-query"

import { SubmitButton } from "../../components/SubmitButton"
import WizardFooter from "../../components/WizardFooter"
import EligibilityQuizFormInput from "../EligibilityQuiz/EligibilityQuizFormInput"
import LiabilityInsurancePropertyCompleted from "./LiabilityInsurancePropertyCompleted"
import { useAccountId, useUpdateInsuranceWizard } from "../../hooks"
import { LiabilityInsuranceTypes } from "@/types/liabilityInsuranceWizard"

export interface LiabilityInsurancePropertyProps {
  handlePrevious: () => void
  handleNext: () => void
  insuranceWizardData: LiabilityInsuranceTypes
}

const LiabilityInsuranceProperty = ({
  handlePrevious,
  handleNext,
  insuranceWizardData,
}: LiabilityInsurancePropertyProps) => {
  const accountId = useAccountId()
  const queryClient = useQueryClient()
  const navigate = useNavigate()

  const { mutateAsync: updateWizard, isPending: isUpdatingWizard } =
    useUpdateInsuranceWizard(queryClient, accountId)

  const currentQuestion = useMemo(() => {
    return (
      insuranceWizardData?.questionnaire_attestations?.find(
        (attestation) =>
          Number(attestation.attestation_id) ===
          insuranceWizardData?.next_questionnaire_attestation_id
      ) || null
    )
  }, [
    insuranceWizardData?.next_questionnaire_attestation_id,
    insuranceWizardData?.questionnaire_attestations,
  ])

  const handleSubmit = useCallback(
    async (
      values: { value: string },
      { resetForm }: FormikHelpers<{ value: string }>
    ) => {
      if (!currentQuestion?.attestation_id) return

      await updateWizard(
        {
          wizard_key: "insurance",
          att_data: {
            attestation_id: currentQuestion.attestation_id,
            value: values.value,
          },
        },
        {
          onSuccess: (data) => {
            const nextId = data?.next_questionnaire_attestation_id

            if (nextId === "complete") {
              handleNext()
            } else if (nextId === "get_in_touch") {
              navigate("/get-in-touch")
            }
          },
        }
      )

      resetForm()
    },
    [currentQuestion, updateWizard, handleNext, navigate]
  )

  if (insuranceWizardData?.next_questionnaire_attestation_id === "complete") {
    return (
      <LiabilityInsurancePropertyCompleted
        handlePrevious={handlePrevious}
        handleNext={handleNext}
      />
    )
  }

  if (!currentQuestion) return null

  return (
    <div
      className="pt-10 lg:pt-13 pb-31"
      data-testid="liability-insurance-property"
    >
      <Formik initialValues={{ value: "" }} onSubmit={handleSubmit}>
        {({ isSubmitting, dirty }) => (
          <Form>
            <div className="container max-w-[844px]">
              <h2
                className="text-charcoal-500 text-xl lg:text-3xl text-center font-display leading-[120%] md:leading-[124%] tracking-[0.4px] md:tracking-[0.6px] mt-7"
                data-testid="current-question-heading"
              >
                {currentQuestion.attestation}
              </h2>

              <div className="max-w-[500px] mx-auto md:mt-9">
                <EligibilityQuizFormInput
                  attestation={{ ...currentQuestion, id: "value" }}
                />
              </div>
            </div>

            <WizardFooter>
              <div className="flex gap-4 ml-auto">
                <button
                  type="button"
                  className="liability-insurance-property-go-back-button btn2 btn2-outline-primary font-bold tracking-[0.32px] whitespace-nowrap"
                  onClick={handlePrevious}
                  data-testid="property-go-back-btn"
                >
                  Go Back
                </button>

                <SubmitButton
                  className="liability-insurance-property-next-button btn2 btn2-primary font-bold tracking-[0.32px] whitespace-nowrap"
                  isSubmitting={isSubmitting || isUpdatingWizard}
                  disabled={!dirty}
                  data-testid="property-next-btn"
                >
                  Next
                </SubmitButton>
              </div>
            </WizardFooter>
          </Form>
        )}
      </Formik>
    </div>
  )
}

export default LiabilityInsuranceProperty
